export type AmPm = 'AM' | 'PM';

export interface TimeParts {
  hour: number; // display hour (1-12 in 12h mode, 0-23 in 24h mode)
  minute: number;
  ampm: AmPm;
}

/** Minute options offered by the task time picker (15-minute increments). */
export const MINUTE_OPTIONS = [0, 15, 30, 45];

/** Minute options at the given step, e.g. minuteOptions(5) → [0,5,…,55]. */
export function minuteOptions(step: number): number[] {
  const out: number[] = [];
  for (let m = 0; m < 60; m += step) out.push(m);
  return out;
}

/** Hour options for the given clock format: 0-23 (24h) or 1-12 (12h). */
export function hourOptions(use24h: boolean): number[] {
  return use24h
    ? Array.from({ length: 24 }, (_, i) => i)
    : Array.from({ length: 12 }, (_, i) => i + 1);
}

/**
 * Split a stored 24-hour "HH:MM" into display parts for the given clock format.
 * Returns null for an empty/invalid value (i.e. "all day", no time).
 */
export function splitTime(hhmm: string, use24h: boolean): TimeParts | null {
  if (!hhmm) return null;
  const [h24, minute] = hhmm.split(':').map(Number);
  if (Number.isNaN(h24) || Number.isNaN(minute)) return null;
  const ampm: AmPm = h24 >= 12 ? 'PM' : 'AM';
  const hour = use24h ? h24 : (h24 % 12 === 0 ? 12 : h24 % 12);
  return { hour, minute, ampm };
}

/** Combine display parts back into a stored 24-hour "HH:MM". */
export function combineTime(hour: number, minute: number, ampm: AmPm, use24h: boolean): string {
  let h = hour;
  if (!use24h) {
    if (ampm === 'AM') h = hour === 12 ? 0 : hour;
    else h = hour === 12 ? 12 : hour + 12;
  }
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}
